import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Modal, Pressable, View, ActivityIndicator, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, ThemedText, Caption, Body } from '@/components/ui/Text';
import {
  checkInboxStatus,
  initiateInboxAuth,
  saveImapSettings,
  type InboxProviderId,
} from '@/services/inboxSync';
import { ImapSettingsModal } from '@/components/planning/ImapSettingsModal';
import { completeOAuthWebSession } from '@/utils/completeOAuthWebSession';

const PROVIDER_LABELS: Record<InboxProviderId, string> = {
  gmail: 'Gmail',
  outlook: 'Outlook',
  icloud: 'iCloud',
};

const PROVIDER_ICONS: Record<InboxProviderId, keyof typeof Ionicons.glyphMap> = {
  gmail: 'mail-outline',
  outlook: 'mail-outline',
  icloud: 'cloud-outline',
};

type ProviderState = {
  connected: boolean | null;
  loading: boolean;
  error?: string;
};

type ConnectedServicesModalProps = {
  visible: boolean;
  onClose: () => void;
};

export function ConnectedServicesModal({ visible, onClose }: ConnectedServicesModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [statuses, setStatuses] = useState<Record<InboxProviderId, ProviderState>>({
    gmail: { connected: null, loading: false },
    outlook: { connected: null, loading: false },
    icloud: { connected: null, loading: false },
  });
  const [imapVisible, setImapVisible] = useState(false);

  const refreshStatuses = useCallback(async () => {
    const providers: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];

    // mark all as loading (but keep last known connection state for nicer UX)
    setStatuses((prev) => {
      const next = { ...prev };
      for (const p of providers) {
        next[p] = { ...prev[p], loading: true, error: undefined };
      }
      return next;
    });

    await Promise.all(
      providers.map(async (provider) => {
        try {
          const status = await checkInboxStatus(provider);
          setStatuses((prev) => ({
            ...prev,
            [provider]: {
              connected: status.connected ?? false,
              loading: false,
            },
          }));
        } catch (err) {
          // iCloud often errors when not configured; treat as "not connected" but keep it quiet.
          setStatuses((prev) => ({
            ...prev,
            [provider]: {
              connected: false,
              loading: false,
              error: err instanceof Error ? err.message : undefined,
            },
          }));
        }
      }),
    );
  }, []);

  useEffect(() => {
    if (!visible) return;
    void refreshStatuses();
  }, [visible, refreshStatuses]);

  const connectedProviders = useMemo(
    () =>
      (['gmail', 'outlook', 'icloud'] as InboxProviderId[]).filter(
        (p) => statuses[p].connected === true,
      ),
    [statuses],
  );

  const handleConnect = useCallback(
    async (provider: InboxProviderId) => {
      if (provider === 'icloud') {
        setImapVisible(true);
        return;
      }

      // Web: open a placeholder window synchronously to avoid popup blockers.
      let popup: Window | null = null;
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        try {
          popup = window.open('about:blank', '_blank');
        } catch {
          popup = null;
        }
      }

      setStatuses((prev) => ({ ...prev, [provider]: { ...prev[provider], loading: true, error: undefined } }));
      try {
        const authResponse = await initiateInboxAuth(provider);
        if (!authResponse?.authUrl) {
          popup?.close?.();
          Alert.alert('Connect email', authResponse?.message || 'Unable to start authorization for this provider.');
          return;
        }

        if (Platform.OS === 'web') {
          if (popup) {
            try {
              popup.location.href = authResponse.authUrl;
            } catch {
              window.location.href = authResponse.authUrl;
            }
          } else {
            window.location.href = authResponse.authUrl;
          }
          Alert.alert(
            'Continue in browser',
            authResponse.message || 'Complete the connection flow and return to Tripvio.',
          );
        } else {
          const canOpen = await Linking.canOpenURL(authResponse.authUrl);
          if (canOpen) {
            const redirectUrl = Linking.createURL(
              provider === 'gmail' ? 'gmail-callback' : 'outlook-callback',
            );
            const result = await WebBrowser.openAuthSessionAsync(authResponse.authUrl, redirectUrl);
            completeOAuthWebSession();
            if (result.type !== 'success') {
              Alert.alert('Authentication cancelled', 'Return to Tripvio and try connecting again.');
              return;
            }
            // Success returns via deep link; the app will refresh connection status shortly.
          } else {
            Alert.alert('Connect email', 'Unable to open the authorization page automatically.');
          }
        }
      } catch (error) {
        popup?.close?.();
        console.error('[ConnectedServicesModal] Error connecting provider:', error);
        Alert.alert('Error', 'Unable to connect email provider. Please try again.');
      } finally {
        setStatuses((prev) => ({ ...prev, [provider]: { ...prev[provider], loading: false } }));
      }
    },
    [],
  );

  const handleSaveImap = useCallback(
    async (settings: {
      host: string;
      username: string;
      password: string;
      port: number;
      secure: boolean;
      mailbox: string;
    }) => {
      try {
        await saveImapSettings({ ...settings, providerId: 'icloud' });
        setImapVisible(false);
        Alert.alert('Mailbox connected', 'Your iCloud/IMAP credentials were saved.');
        await refreshStatuses();
      } catch (error) {
        console.error('[ConnectedServicesModal] Failed to save IMAP settings', error);
        Alert.alert(
          'Unable to save credentials',
          error instanceof Error ? error.message : 'Please double-check your mailbox details and try again.',
        );
      }
    },
    [refreshStatuses],
  );

  return (
    <>
      <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
        <View style={{ flex: 1, backgroundColor: theme.colors.modalOverlay, justifyContent: 'flex-end' }}>
          <Pressable style={{ flex: 0.15 }} onPress={onClose} />
          <View
            style={{
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: theme.radii.xl,
              borderTopRightRadius: theme.radii.xl,
              paddingBottom: insets.bottom,
            }}
          >
            <View
              style={{
                padding: theme.spacing(2),
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.borderMuted,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: theme.spacing(1),
              }}
            >
              <View style={{ flex: 1 }}>
                <SubHeading>Connected services</SubHeading>
                <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                  {connectedProviders.length > 0
                    ? `${connectedProviders.length} connected`
                    : 'Connect an email provider to import bookings'}
                </Caption>
              </View>
              <Pressable onPress={onClose} accessibilityRole="button" accessibilityLabel="Close connected services">
                <Ionicons name="close" size={22} color={theme.colors.textSecondary} />
              </Pressable>
            </View>

            <View style={{ padding: theme.spacing(2), gap: theme.spacing(1.25) }}>
              {(Object.keys(PROVIDER_LABELS) as InboxProviderId[]).map((provider) => {
                const s = statuses[provider];
                const isConnected = s.connected === true;
                const actionLabel =
                  provider === 'icloud'
                    ? isConnected
                      ? 'Edit'
                      : 'Connect'
                    : isConnected
                      ? 'Change account'
                      : 'Connect';

                return (
                  <Card
                    key={provider}
                    variant="outlined"
                    style={{
                      borderColor: isConnected ? theme.colors.primary : theme.colors.borderMuted,
                      borderWidth: isConnected ? 1.5 : 1,
                    }}
                  >
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5) }}>
                      <View
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: theme.radii.md,
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isConnected ? theme.colors.primary + '14' : theme.colors.borderMuted,
                        }}
                      >
                        {s.loading ? (
                          <ActivityIndicator size="small" color={theme.colors.primary} />
                        ) : (
                          <Ionicons
                            name={PROVIDER_ICONS[provider]}
                            size={22}
                            color={isConnected ? theme.colors.primary : theme.colors.textSecondary}
                          />
                        )}
                      </View>

                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                          <ThemedText variant="bodyStrong">{PROVIDER_LABELS[provider]}</ThemedText>
                          {isConnected && (
                            <Ionicons
                              name="checkmark-circle"
                              size={16}
                              color={theme.colors.success || theme.colors.primary}
                            />
                          )}
                        </View>
                        <Caption color={theme.colors.textSecondary}>
                          {isConnected ? 'Connected' : provider === 'icloud' ? 'Connect with IMAP settings' : 'Connect with OAuth'}
                        </Caption>
                        {!!s.error && !isConnected && provider !== 'icloud' && (
                          <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25), fontSize: 11 }}>
                            {s.error}
                          </Caption>
                        )}
                      </View>

                      <Button
                        label={actionLabel}
                        variant={isConnected ? 'secondary' : 'primary'}
                        size="small"
                        onPress={() => handleConnect(provider)}
                        disabled={s.loading}
                        style={{ minWidth: 120 }}
                      />
                    </View>
                  </Card>
                );
              })}

              <Card variant="outlined" style={{ marginTop: theme.spacing(0.5) }}>
                <View style={{ flexDirection: 'row', gap: theme.spacing(1.25) }}>
                  <Ionicons name="information-circle-outline" size={18} color={theme.colors.primary} />
                  <View style={{ flex: 1 }}>
                    <Body variant="bodyStrong">How this works</Body>
                    <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                      Connected email providers can be used to import bookings into any trip via “Email sync”.
                    </Caption>
                  </View>
                </View>
              </Card>
            </View>
          </View>
        </View>
      </Modal>

      <ImapSettingsModal
        visible={imapVisible}
        onClose={() => setImapVisible(false)}
        onSave={handleSaveImap}
      />
    </>
  );
}


